import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Plus, CheckCircle2, Calculator, Upload, GripVertical, Trash2 } from 'lucide-react';
import CustomerSelectorField from './CustomerSelectorField';
import SalespersonField from './SalespersonField';
import {
  createAccountSalesOrder,
  updateAccountSalesOrder,
  getAccountSalesOrderById,
  getAccountSalesOrderItems,
  createAccountSalesOrderItem,
  deleteAccountSalesOrderItem,
  uploadMenuFiles,
  deleteMenuFile,
  getAccountQuotes,
} from '../core/_requests';
import { useAccountCustomers } from '../core/useAccountCustomers';
import { setupAxios } from '../../auth';
import SearchableSelect from '../../../shared/components/SearchableSelect'
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

type UploadedFile = {
  originalName: string;
  fileName: string;
  size: number;
  mimeType?: string;
  path: string;
  url?: string;
};

export default function AccountsNewSalesOrder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const orderId = id ? Number(id) : null;
  const isEditing = orderId !== null && !Number.isNaN(orderId);
  const [saving, setSaving] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const customersHook = useAccountCustomers();
  const { customers } = customersHook;

  const location = useLocation();
  const quoteData = (location.state as any)?.quoteData;

  // Form State
  const [selectedCustomerId, setSelectedCustomerId] = useState(quoteData?.customerId ? String(quoteData.customerId) : '');
  const [selectedCustomerName, setSelectedCustomerName] = useState(quoteData?.customerName || '');
  const [allQuotes, setAllQuotes] = useState<any[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>('');
  const [salesOrderNumber, setSalesOrderNumber] = useState(`SO-${String(Date.now()).slice(-5)}`);
  const [referenceNumber, setReferenceNumber] = useState(quoteData?.quoteNumber || '');
  const [salesOrderDate, setSalesOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [expectedShipmentDate, setExpectedShipmentDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState(quoteData?.paymentTerms || 'Due on Receipt');
  const [deliveryMethod, setDeliveryMethod] = useState(quoteData?.deliveryMethod || '');
  const [salesperson, setSalesperson] = useState(quoteData?.salesperson || '');
  const [orderStatus, setOrderStatus] = useState('Draft');
  
  // Line Items
  const initialItems = quoteData?.items && quoteData.items.length > 0 
    ? [...quoteData.items].sort((a: any, b: any) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0)).map((item: any, idx: number) => ({
        id: idx + 1,
        description: String(item.description || item.itemName || ''),
        quantity: String(item.quantity ?? '1.00'),
        rate: String(item.rate ?? '0.00'),
      }))
    : [{ id: 1, description: '', quantity: '1.00', rate: '0.00' }];
  const [items, setItems] = useState(initialItems);

  // Totals & Settings
  const [customerNotes, setCustomerNotes] = useState(quoteData?.customerNotes || 'Thanks for your business.');
  const [termsConditions, setTermsConditions] = useState(quoteData?.termsConditions || '');
  const [discountPercent, setDiscountPercent] = useState(String(quoteData?.discountPercent ?? 0));
  const [taxType, setTaxType] = useState(quoteData?.taxType || 'TDS');
  
  const parsedRate = quoteData?.taxName?.match(/(\d+(?:\.\d+)?)\s*%/);
  const [taxRate, setTaxRate] = useState(parsedRate ? parsedRate[1] : '');
  const [adjustment, setAdjustment] = useState(String(quoteData?.adjustment ?? 0));
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const buildUploadedFiles = (raw: any): UploadedFile[] => {
    const entries = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? raw.split(',')
        : [];

    return entries
      .map((entry) => (typeof entry === 'string' ? entry.replace(/\\/g, '/').trim() : ''))
      .filter(Boolean)
      .map((entry) => {
        const fileName = entry.split('/').pop() || entry;
        let url = entry;
        if (!/^https?:\/\//i.test(entry)) {
          const basePath = entry.startsWith('/') ? entry : `/${entry}`;
          const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
          url = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return {
          originalName: fileName,
          fileName,
          size: 0,
          mimeType: '',
          path: entry,
          url,
        } as UploadedFile;
      });
  };

  // Load existing order for edit mode
  useEffect(() => {
    if (!isEditing || orderId == null) return;
    let isActive = true;
    setLoadingOrder(true);
    Promise.allSettled([getAccountSalesOrderById(orderId), getAccountSalesOrderItems(orderId)])
      .then(([orderRes, itemsRes]) => {
        if (!isActive) return;
        if (orderRes.status === 'fulfilled') {
          const data = orderRes.value;
          setSelectedCustomerId(data.customerId ? String(data.customerId) : '');
          setSelectedCustomerName(data.customerName || '');
          setSalesOrderNumber(data.orderNumber || `SO-${String(Date.now()).slice(-5)}`);
          setReferenceNumber(data.referenceNumber || '');
          setSalesOrderDate(data.orderDate ? new Date(data.orderDate).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
          setExpectedShipmentDate(data.shipmentDate ? new Date(data.shipmentDate).toISOString().slice(0, 10) : '');
          setPaymentTerms(data.paymentTerms || 'Due on Receipt');
          setDeliveryMethod(data.deliveryMethod || '');
          setSalesperson(data.salesperson || '');
          setOrderStatus(data.status || 'Draft');
          setCustomerNotes(data.customerNotes || '');
          setTermsConditions(data.termsConditions || '');
          setDiscountPercent(String(data.discountPercent ?? 0));
          setTaxType(data.taxType || 'TDS');
          const parsedRate = data.taxName?.match(/(\d+(?:\.\d+)?)\s*%/);
          setTaxRate(parsedRate ? parsedRate[1] : '');
          setAdjustment(String(data.adjustment ?? 0));
          
          const filePathValue = data.filePath ?? data.filePaths ?? data.file_path ?? data.filepath ?? '';
          const existingFiles = buildUploadedFiles(filePathValue);
          setUploadedFiles(existingFiles);

          const rawItems = itemsRes.status === 'fulfilled' && Array.isArray(itemsRes.value) && itemsRes.value.length > 0
            ? itemsRes.value
            : Array.isArray(data.items) ? data.items : [];
          if (rawItems.length > 0) {
            const sorted = [...rawItems].sort((a, b) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0));
            setItems(sorted.map((item, idx) => ({
              id: Number(item.id ?? idx + 1),
              description: String(item.description || item.itemName || ''),
              quantity: String(item.quantity ?? '1.00'),
              rate: String(item.rate ?? '0.00'),
            })));
          }
        }
      })
      .catch(err => console.error('Failed to load sales order:', err))
      .finally(() => { if (isActive) setLoadingOrder(false); });
    return () => { isActive = false; };
  }, [orderId, isEditing]);

  useEffect(() => {
    getAccountQuotes()
      .then(res => {
        if (Array.isArray(res)) setAllQuotes(res);
      })
      .catch(err => console.error('Failed to load quotes:', err));
  }, []);

  const handleQuoteSelect = (quoteId: string) => {
    setSelectedQuoteId(quoteId);
    if (!quoteId) return;
    
    const quote = allQuotes.find(q => String(q.id) === quoteId);
    if (!quote) return;

    setReferenceNumber(quote.quoteNumber || '');
    setPaymentTerms(quote.paymentTerms || 'Due on Receipt');
    setDeliveryMethod(quote.deliveryMethod || '');
    setSalesperson(quote.salesperson || '');
    setCustomerNotes(quote.customerNotes || 'Thanks for your business.');
    setTermsConditions(quote.termsConditions || '');
    setDiscountPercent(String(quote.discountPercent ?? 0));
    setTaxType(quote.taxType || 'TDS');
    
    const parsedRate = quote.taxName?.match(/(\d+(?:\.\d+)?)\s*%/);
    setTaxRate(parsedRate ? parsedRate[1] : '');
    setAdjustment(String(quote.adjustment ?? 0));

    if (quote.items && Array.isArray(quote.items)) {
      const sorted = [...quote.items].sort((a: any, b: any) => (a.itemOrder ?? 0) - (b.itemOrder ?? 0));
      setItems(sorted.map((item: any, idx: number) => ({
        id: Number(item.id ?? idx + 1),
        description: String(item.description || item.itemName || ''),
        quantity: String(item.quantity ?? '1.00'),
        rate: String(item.rate ?? '0.00'),
      })));
    }
  };

  // Calculations
  const toNumber = (val) => {
    const num = parseFloat(val);
    return Number.isFinite(num) ? num : 0;
  };
  
  const getRowAmount = (row) => toNumber(row.quantity) * toNumber(row.rate);
  const subTotal = items.reduce((sum, row) => sum + getRowAmount(row), 0);
  const discountAmount = subTotal * (toNumber(discountPercent) / 100);
  const adjustmentAmount = toNumber(adjustment);
  const taxableBase = Math.max(0, subTotal - discountAmount + adjustmentAmount);
  
  const activeTaxOptions = taxType === 'TDS' ? [
    { label: 'Dividend [10%]', rate: 10 },
    { label: 'Other Interest [10%]', rate: 10 },
    { label: 'Contractors [2%]', rate: 2 },
  ] : [
    { label: 'TCS on Goods [0.1%]', rate: 0.1 }
  ];
  
  const selectedTax = activeTaxOptions.find(opt => opt.rate === Number(taxRate));
  const taxAmountRaw = taxableBase * ((selectedTax?.rate || 0) / 100);
  const taxAmount = taxType === 'TDS' ? -taxAmountRaw : taxAmountRaw;
  const total = taxableBase + taxAmount;



  const buildLineItemPayload = (row, order) => ({
    itemOrder: order,
    itemName: row.description.trim() || null,
    description: row.description.trim() || null,
    quantity: toNumber(row.quantity),
    rate: toNumber(row.rate),
    amount: getRowAmount(row),
  });

  const syncLineItems = async (targetId: number) => {
    let existing: any[] = [];
    try { existing = await getAccountSalesOrderItems(targetId); } catch (err) { console.warn('Failed to load existing items:', err); }
    if (existing.length > 0) {
      await Promise.all(existing.map((item: any) => deleteAccountSalesOrderItem(targetId, item.id)));
    }
    const cleaned = items
      .filter(row => row.description.trim().length > 0 || toNumber(row.rate) > 0 || toNumber(row.quantity) !== 1)
      .map((row, index) => buildLineItemPayload(row, index + 1));
    if (cleaned.length > 0) {
      await Promise.all(cleaned.map(item => createAccountSalesOrderItem(targetId, item)));
    }
  };

  const handleSave = async (statusOverride?: string) => {
    setSaving(true);
    try {
      const customer = customers.find(c => String(c.id) === selectedCustomerId);
      const customerName = customer?.displayName || customer?.name || customer?.companyName || selectedCustomerName || '';
      const customerId = selectedCustomerId ? Number(selectedCustomerId) : null;
      const orderDateIso = salesOrderDate || new Date().toISOString().slice(0, 10);
      const shipmentDateIso = expectedShipmentDate || null;
      const status = typeof statusOverride === 'string' ? statusOverride : orderStatus || 'Draft';
      const uploadedPaths = uploadedFiles.map((file) => file.path).filter(Boolean);

      const payload = {
        orderDate: orderDateIso,
        shipmentDate: shipmentDateIso || null,
        orderNumber: salesOrderNumber,
        referenceNumber: referenceNumber || null,
        customerId,
        customerName,
        paymentTerms,
        deliveryMethod,
        salesperson: salesperson || null,
        subject: null,
        customerNotes: customerNotes || null,
        termsConditions: termsConditions || null,
        status,
        subTotal,
        discountPercent: toNumber(discountPercent),
        discountAmount,
        taxType,
        taxName: selectedTax?.label || null,
        taxAmount: Math.abs(taxAmountRaw),
        adjustment: adjustmentAmount,
        filePath: uploadedPaths.length ? uploadedPaths.join(',') : null,
        total,
      };

      let targetId = orderId || null;
      if (isEditing && orderId) {
        await updateAccountSalesOrder(orderId, payload);
      } else {
        const created = await createAccountSalesOrder(payload);
        targetId = (created as any)?.id ?? (created as any)?.data?.id ?? null;
      }

      if (targetId) {
        await syncLineItems(targetId);
      }
      navigate(-1);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    setItems((prev) => {
      const nextId = prev.reduce((max, row) => Math.max(max, row.id), 0) + 1;
      return [...prev, { id: nextId, description: '', quantity: '1.00', rate: '0.00' }];
    });
  };

  const updateRow = (id, field, value) => {
    setItems(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removeRow = (id) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(row => row.id !== id));
    }
  };
  
  const triggerDocumentSelect = () => {
    documentInputRef.current?.click();
  };

  const handleDocumentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [] as File[]);
    if (!files.length) {
      return;
    }

    setUploadingFiles(true);
    setUploadError(null);

    try {
      const uploaded = await uploadMenuFiles('sales_orders', files);
      const baseUrl = api.defaults.baseURL?.replace(/\/?api\/?$/, '').replace(/\/$/, '') || '';
      const formattedUploads = (uploaded as UploadedFile[]).map((f) => {
        let fileUrl = f.url || f.path;
        if (!/^https?:\/\//i.test(fileUrl)) {
          const basePath = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
          fileUrl = `${baseUrl}${basePath.startsWith('/uploads') ? basePath : `/uploads${basePath}`}`;
        }
        return { ...f, url: fileUrl };
      });
      setUploadedFiles((prev) => [...prev, ...formattedUploads]);
    } catch (err) {
      const message = 'Upload failed.';
      setUploadError(message);
    } finally {
      setUploadingFiles(false);
      event.target.value = '';
    }
  };

  const handleDeleteFile = async (file: UploadedFile) => {
    try {
      if (file.fileName) {
        await deleteMenuFile('sales_orders', file.fileName);
      }
      setUploadedFiles((prev) => prev.filter((f) => f.path !== file.path));
    } catch (err) {
      const message = 'Delete failed.';
      setUploadError(message);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition-all hover:border-slate-300";
  const labelClass = "block text-[11px] font-bold text-slate-600 mb-1.5";

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts')}>ACCOUNTS</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-400 hover:text-blue-600 cursor-pointer transition-colors" onClick={() => navigate('/accounts/sales')}>SALES</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-blue-600">SALES ORDERS</span>
                  </div>
                  <h1 className="text-3xl font-extrabold text-slate-900 leading-tight">{isEditing ? 'Edit Sales Order' : 'New Sales Order'}</h1>
                  <p className="text-sm font-medium text-slate-500 mt-1">{isEditing ? 'Update the sales order' : 'Create a new sales order'}</p>
                  {loadingOrder && <p className="text-xs text-slate-400 mt-1">Loading order details...</p>}
                </div>
              </div>

              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-[20px] border border-slate-200 shadow-sm p-6 md:p-8">
                
                {/* Primary Details Section */}
                <div className="mb-8 max-w-4xl space-y-6">
                  {/* Customer Name */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Customer Name</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <CustomerSelectorField 
                        value={selectedCustomerId}
                        onChange={(id, name) => { setSelectedCustomerId(id); setSelectedCustomerName(name); setSelectedQuoteId(''); }}
                        width="100%"
                        customersHook={customersHook}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  
                  {/* Quote Selection */}
                  {selectedCustomerId && (
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                        <label className="text-[13px] font-bold text-slate-700">Select Accepted Quote</label>
                      </div>
                      <div className="flex-1 relative">
                        <SearchableSelect 
                          value={selectedQuoteId} 
                          onChange={e => handleQuoteSelect(e.target.value)} 
                          className={inputClass}
                        >
                          <option value="">-- Optional: Select a Quote --</option>
                          {allQuotes
                            .filter(q => String(q.customerId) === selectedCustomerId && q.status === 'Accepted')
                            .map(q => (
                              <option key={q.id} value={String(q.id)}>
                                {q.quoteNumber} (Total: ${Number(q.total).toFixed(2)})
                              </option>
                            ))
                          }
                        </SearchableSelect>
                      </div>
                    </div>
                  )}

                  {/* Sales Order# */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Sales Order#</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1 relative">
                      <input type="text" value={salesOrderNumber} onChange={e => setSalesOrderNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Reference# */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Reference#</label>
                    </div>
                    <div className="flex-1">
                      <input type="text" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Sales Order Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Sales Order Date</label>
                      <span className="text-red-500 font-bold">*</span>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={salesOrderDate} onChange={e => setSalesOrderDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Expected Shipment Date */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Expected Shipment Date</label>
                    </div>
                    <div className="flex-1">
                      <input type="date" value={expectedShipmentDate} onChange={e => setExpectedShipmentDate(e.target.value)} className={inputClass} />
                    </div>
                  </div>

                  {/* Payment Terms */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Payment Terms</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} className={inputClass}>
                        <option value="Due on Receipt">Due on Receipt</option>
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 45">Net 45</option>
                        <option value="Net 60">Net 60</option>
                        <option value="Due end of the month">Due end of the month</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Delivery Method */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Delivery Method</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={deliveryMethod} onChange={e => setDeliveryMethod(e.target.value)} className={inputClass}>
                        <option value="">Select Delivery Method</option>
                        <option value="Courier">Courier</option>
                        <option value="In-Person">In-Person</option>
                        <option value="Digital">Digital Delivery</option>
                      </SearchableSelect>
                    </div>
                  </div>

                  {/* Salesperson */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Salesperson</label>
                    </div>
                    <div className="flex-1">
                      <SalespersonField value={salesperson} onChange={setSalesperson} className={inputClass} />
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-48 flex items-center gap-1.5 shrink-0">
                      <label className="text-[13px] font-bold text-slate-700">Status</label>
                    </div>
                    <div className="flex-1">
                      <SearchableSelect value={orderStatus} onChange={e => setOrderStatus(e.target.value)} className={inputClass}>
                        <option value="Draft">Draft</option>
                        <option value="Open">Open</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Fulfilled">Fulfilled</option>
                        <option value="Cancelled">Cancelled</option>
                      </SearchableSelect>
                    </div>
                  </div>
                </div>

                {/* Item Table */}
                <div className="mb-8 overflow-visible border border-slate-200 rounded-xl shadow-sm">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between rounded-t-xl">
                    <h3 className="text-sm font-bold text-slate-800">Item Table</h3>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-175">
                      <thead>
                        <tr className="bg-white border-b border-slate-100">
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-[45%]">Item Details</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Quantity</th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[20%]">Rate <Calculator className="w-3 h-3 inline-block ml-1 opacity-50" /></th>
                          <th className="px-4 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-right w-[15%]">Amount</th>
                          <th className="px-4 py-3 w-[5%]"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((row) => (
                          <tr key={row.id} className="bg-white group hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-start gap-3">
                                <GripVertical className="w-4 h-4 text-slate-300 mt-2 cursor-grab" />
                                <textarea 
                                  rows={1} 
                                  value={row.description}
                                  onChange={e => updateRow(row.id, 'description', e.target.value)}
                                  placeholder="Type or click to select an item." 
                                  className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all resize-none placeholder:text-slate-400"
                                ></textarea>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input 
                                type="text" 
                                value={row.quantity}
                                onChange={e => updateRow(row.id, 'quantity', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top">
                              <input 
                                type="text" 
                                value={row.rate}
                                onChange={e => updateRow(row.id, 'rate', e.target.value)}
                                className="w-full text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 align-top text-right">
                              <div className="pt-2 text-sm font-bold text-slate-700">
                                {getRowAmount(row).toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 align-top text-center">
                              {items.length > 1 && (
                                <button type="button" onClick={() => removeRow(row.id)} className="p-1.5 mt-0.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2 rounded-b-xl">
                    <button type="button" onClick={addRow} className="text-xs font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-100/50 hover:bg-blue-100 px-3 py-2 rounded-xl transition-colors">
                      <Plus className="w-3.5 h-3.5" /> Add New Row
                    </button>
                  </div>
                </div>

                {/* Additional Details & Totals */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  {/* Left: Notes & Attachments */}
                  <div className="lg:col-span-7 space-y-6">
                    <div>
                      <label className={labelClass}>Customer Notes</label>
                      <textarea 
                        rows={3} 
                        value={customerNotes}
                        onChange={e => setCustomerNotes(e.target.value)}
                        placeholder="Will be displayed on the sales order" 
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Terms & Conditions</label>
                      <textarea 
                        rows={3} 
                        value={termsConditions}
                        onChange={e => setTermsConditions(e.target.value)}
                        placeholder="Enter the terms and conditions" 
                        className={`${inputClass} resize-none`}
                      ></textarea>
                    </div>
                    <div>
                      <label className={labelClass}>Attach File(s) to Sales Order</label>
                      <input
                        ref={documentInputRef}
                        type="file"
                        multiple
                        hidden
                        onChange={handleDocumentChange}
                      />
                      <button 
                        type="button" 
                        onClick={triggerDocumentSelect}
                        disabled={uploadingFiles}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:border-slate-300 rounded-xl text-sm font-bold text-slate-700 transition-colors shadow-sm disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4 text-slate-400" /> {uploadingFiles ? 'Uploading...' : 'Upload File'}
                      </button>
                      <p className="text-[11px] text-slate-400 mt-2 font-medium">Max 5 files, 10MB each</p>
                      {uploadError && <p className="text-[11px] text-red-500 mt-1 font-medium">{uploadError}</p>}
                      {uploadedFiles.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {uploadedFiles.map((file) => (
                            <li key={`${file.fileName}-${file.path || ''}`} className="flex items-center gap-2 group">
                              <a href={file.url || file.path} target="_blank" rel="noopener noreferrer" className="text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                {file.originalName || file.fileName || file.path}
                              </a>
                              <button
                                type="button"
                                onClick={() => handleDeleteFile(file)}
                                className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors opacity-0 group-hover:opacity-100"
                                title="Delete file"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Right: Totals Block */}
                  <div className="lg:col-span-5">
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                      
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-bold text-slate-700">Sub Total</span>
                        <span className="text-sm font-bold text-slate-900">{subTotal.toFixed(2)}</span>
                      </div>
                      
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-semibold text-slate-500 w-24">Discount</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <div className="flex items-center w-24 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-50 transition-all">
                            <input type="text" value={discountPercent} onChange={e => setDiscountPercent(e.target.value)} className="w-full text-right px-2 py-1.5 text-sm outline-none" />
                            <div className="px-2 py-1.5 bg-slate-50 border-l border-slate-200 text-sm font-medium text-slate-500">%</div>
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{discountAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3 w-32">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="taxType" checked={taxType === 'TDS'} onChange={() => { setTaxType('TDS'); setTaxRate(''); }} className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300" />
                            <span className="text-[12px] font-semibold text-slate-600">TDS</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="taxType" checked={taxType === 'TCS'} onChange={() => { setTaxType('TCS'); setTaxRate(''); }} className="w-3.5 h-3.5 text-blue-600 focus:ring-blue-500 border-slate-300" />
                            <span className="text-[12px] font-semibold text-slate-600">TCS</span>
                          </label>
                        </div>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <SearchableSelect value={taxRate} onChange={e => setTaxRate(e.target.value)} className="w-40 px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 transition-all">
                            {activeTaxOptions.length > 0 ? (
                              <>
                                <option value="">Select a Tax</option>
                                {activeTaxOptions.map(opt => (
                                  <option key={opt.label} value={String(opt.rate)}>{opt.label}</option>
                                ))}
                              </>
                            ) : (
                              <option value="" disabled>No results found</option>
                            )}
                          </SearchableSelect>
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{taxAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-6">
                        <span className="text-sm font-semibold text-slate-500 w-24">Adjustment</span>
                        <div className="flex-1 flex items-center justify-end gap-3">
                          <input type="text" value={adjustment} onChange={e => setAdjustment(e.target.value)} className="w-24 text-right px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all" />
                          <span className="text-sm font-medium text-slate-700 w-20 text-right">{adjustmentAmount.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                        <span className="text-lg font-black text-slate-900">Total ($)</span>
                        <span className="text-xl font-black text-blue-600">{total.toFixed(2)}</span>
                      </div>
                      
                    </div>
                  </div>
                </div>

                {/* Footer Actions */}
                <div className="pt-8 mt-8 border-t border-slate-100 flex items-center gap-3">
                  <button 
                    type="button" 
                    onClick={() => handleSave()} 
                    disabled={saving} 
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : isEditing ? 'Update Order' : 'Save Order'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

